import React, { useState } from 'react';
import { ShieldAlert, UploadCloud, CheckCircle2, AlertTriangle, Play, Pause, XCircle } from 'lucide-react';
import { Card, Button, ProgressBar, SystemBadge } from '../components/ui/Primitives';
export function ManualDNC() {
  const [action, setAction] = useState<'set' | 'remove'>('set');
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['bonzo', 'ghl']);
  const [phoneInput, setPhoneInput] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const phoneCount = phoneInput.split('\n').filter(line => line.trim().length > 0).length;
  const canRun = phoneCount > 0 && selectedPlatforms.length > 0;
  const togglePlatform = (sys: string) => {
    setSelectedPlatforms(prev => prev.includes(sys) ? prev.filter(p => p !== sys) : [...prev, sys]);
  };
  const handleRunClick = () => {
    if (action === 'set') {
      setShowConfirmModal(true);
    } else {
      startJob();
    }
  };
  const startJob = () => {
    setShowConfirmModal(false);
    setIsRunning(true);
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          return 100;
        }
        return p + 5;
      });
    }, 500);
  };
  const resetJob = () => {
    setIsRunning(false);
    setProgress(0);
    setPhoneInput('');
  };
  return <div className="max-w-6xl mx-auto h-full flex flex-col relative" data-id="element-444">
      {showConfirmModal && <div className="absolute inset-0 z-50 flex items-center justify-center" data-id="element-445">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setShowConfirmModal(false)} data-id="element-446" />
          <Card className="w-[400px] p-6 relative z-10 shadow-2xl animate-in zoom-in-95" data-id="element-447">
            <div className="flex items-center gap-3 text-red-600 mb-4" data-id="element-448">
              <AlertTriangle className="w-6 h-6" data-id="element-449" />
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-450">
                Confirm Destructive Action
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-6" data-id="element-451">
              You are about to mark <strong data-id="element-452">{phoneCount}</strong> contacts as Do
              Not Contact across <strong data-id="element-453">{selectedPlatforms.length}</strong>{' '}
              platforms. This will trigger downstream syncs.
            </p>
            <div className="flex justify-end gap-3" data-id="element-454">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} data-id="element-455">
                Cancel
              </Button>
              <Button variant="danger" onClick={startJob} data-id="element-456">
                Confirm — Mark as DNC
              </Button>
            </div>
          </Card>
        </div>}

      <div className="mb-6" data-id="element-457">
        <h1 className="text-xl font-semibold text-slate-900" data-id="element-458">
          Manual DNC Operations
        </h1>
        <p className="text-sm text-slate-500" data-id="element-459">
          Bulk update Do Not Contact status across platforms.
        </p>
      </div>

      <div className="flex gap-6 flex-1 min-h-0" data-id="element-460">
        {/* Left: Job Builder */}
        <div className="w-1/2 flex flex-col gap-6 overflow-y-auto pr-2 pb-6" data-id="element-461">
          <Card className="p-5" data-id="element-462">
            <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2" data-id="element-463">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs" data-id="element-464">
                1
              </span>
              Input Phone Numbers
            </h2>
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer mb-4" data-id="element-465">
              <UploadCloud className="w-8 h-8 text-slate-400 mx-auto mb-2" data-id="element-466" />
              <p className="text-sm font-medium text-slate-700" data-id="element-467">
                Drop CSV file here
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-468">or click to browse</p>
            </div>
            <div className="relative" data-id="element-469">
              <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-470">
                <div className="w-full border-t border-slate-200" data-id="element-471" />
              </div>
              <div className="relative flex justify-center" data-id="element-472">
                <span className="bg-surface px-2 text-xs text-slate-500" data-id="element-473">
                  OR PASTE NUMBERS
                </span>
              </div>
            </div>
            <textarea className="w-full mt-4 h-32 rounded-lg border border-slate-300 p-3 text-sm font-mono focus:ring-2 focus:ring-electric-500 focus:border-electric-500" placeholder="+1 (555) 123-4567&#10;+1 (555) 987-6543" value={phoneInput} onChange={e => setPhoneInput(e.target.value)} data-id="element-474" />
          </Card>

          <Card className="p-5" data-id="element-475">
            <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2" data-id="element-476">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs" data-id="element-477">
                2
              </span>
              Select Target Platforms
            </h2>
            <div className="grid grid-cols-3 gap-3" data-id="element-478">
              <PlatformSelectCard system="bonzo" selected={selectedPlatforms.includes('bonzo')} onClick={() => togglePlatform('bonzo')} data-id="element-479" />
              <PlatformSelectCard system="ghl" selected={selectedPlatforms.includes('ghl')} onClick={() => togglePlatform('ghl')} data-id="element-480" />
              <PlatformSelectCard system="five9" selected={selectedPlatforms.includes('five9')} onClick={() => togglePlatform('five9')} data-id="element-481" />
            </div>
          </Card>

          <Card className={`p-5 border-2 transition-colors ${action === 'set' ? 'border-red-200 bg-red-50/30' : 'border-emerald-200 bg-emerald-50/30'}`} data-id="element-482">
            <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2" data-id="element-483">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs" data-id="element-484">
                3
              </span>
              Action to Perform
            </h2>
            <div className="flex bg-slate-100 p-1 rounded-lg mb-6" data-id="element-485">
              <button onClick={() => setAction('set')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${action === 'set' ? 'bg-white shadow-sm text-red-600' : 'text-slate-600 hover:text-slate-800'}`} data-id="element-486">
                <ShieldAlert className="w-4 h-4 inline mr-2" data-id="element-487" /> Set DNC
              </button>
              <button onClick={() => setAction('remove')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${action === 'remove' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-600 hover:text-slate-800'}`} data-id="element-488">
                <CheckCircle2 className="w-4 h-4 inline mr-2" data-id="element-489" /> Remove DNC
              </button>
            </div>

            <div className="space-y-3 mb-6" data-id="element-490">
              <label className="flex items-center gap-3" data-id="element-491">
                <input type="checkbox" className="rounded border-slate-300 text-electric-500 focus:ring-electric-500" defaultChecked data-id="element-492" />
                <span className="text-sm text-slate-700" data-id="element-493">
                  Create contact if missing
                </span>
              </label>
              <label className="flex items-center gap-3" data-id="element-494">
                <input type="checkbox" className="rounded border-slate-300 text-electric-500 focus:ring-electric-500" defaultChecked data-id="element-495" />
                <span className="text-sm text-slate-700" data-id="element-496">Skip duplicates</span>
              </label>
            </div>

            {action === 'set' && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3 mb-6" data-id="element-497">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" data-id="element-498" />
                <div className="text-sm text-red-800" data-id="element-499">
                  <span className="font-semibold" data-id="element-500">
                    Destructive Action Warning.
                  </span>{' '}
                  This will mark ~{phoneCount || 0} contacts as Do Not Contact
                  across {selectedPlatforms.length} platforms. This will trigger
                  downstream syncs.
                </div>
              </div>}

            <div className="flex items-center justify-between mb-2" data-id="element-501">
              <span className="text-xs font-medium text-slate-500" data-id="element-502">
                {phoneCount} numbers detected
              </span>
            </div>
            <Button variant={action === 'set' ? 'danger' : 'primary'} className="w-full h-12 text-base" onClick={handleRunClick} disabled={isRunning || !canRun} data-id="element-503">
              {isRunning ? 'Job Running...' : `Run ${action === 'set' ? 'Set DNC' : 'Remove DNC'} Job`}
            </Button>
          </Card>
        </div>

        {/* Right: Live Progress */}
        <div className="w-1/2" data-id="element-504">
          {isRunning ? <Card className="p-6 h-full flex flex-col animate-in fade-in zoom-in-95" data-id="element-505">
              <div className="flex items-center justify-between mb-8" data-id="element-506">
                <div data-id="element-507">
                  <h2 className="text-lg font-semibold text-slate-900" data-id="element-508">
                    {progress === 100 ? 'Job Complete' : 'Job Running'}
                  </h2>
                  <p className="text-sm text-slate-500 font-mono mt-1" data-id="element-509">
                    job_8f72a9bc
                  </p>
                </div>
                {progress < 100 ? <div className="flex gap-2" data-id="element-510">
                    <Button variant="outline" size="sm" data-id="element-511">
                      <Pause className="w-4 h-4" data-id="element-512" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={resetJob} data-id="element-513">
                      <XCircle className="w-4 h-4" data-id="element-514" />
                    </Button>
                  </div> : <Button variant="primary" size="sm" onClick={resetJob} data-id="element-515">
                    Start New Job
                  </Button>}
              </div>

              <div className="mb-8" data-id="element-516">
                <div className="flex justify-between text-sm mb-2" data-id="element-517">
                  <span className="font-medium text-slate-700" data-id="element-518">
                    Overall Progress
                  </span>
                  <span className="font-mono text-electric-600" data-id="element-519">
                    {progress}%
                  </span>
                </div>
                <ProgressBar progress={progress} active={progress < 100} data-id="element-520" />
                {progress < 100 && <div className="text-xs text-slate-500 mt-2 text-right" data-id="element-521">
                    Est. remaining: 2m 14s
                  </div>}
              </div>

              <div className="grid grid-cols-4 gap-4 mb-8" data-id="element-522">
                <StatBox label="Processed" value={Math.floor(phoneCount * (progress / 100))} color="text-slate-900" data-id="element-523" />
                <StatBox label="Success" value={Math.floor(phoneCount * 0.95 * (progress / 100))} color="text-emerald-600" data-id="element-524" />
                <StatBox label="Failed" value={Math.floor(phoneCount * 0.02 * (progress / 100))} color="text-red-600" data-id="element-525" />
                <StatBox label="Skipped" value={Math.floor(phoneCount * 0.03 * (progress / 100))} color="text-amber-600" data-id="element-526" />
              </div>

              <h3 className="text-sm font-semibold text-slate-800 mb-3" data-id="element-527">
                Platform Breakdown
              </h3>
              <div className="space-y-4 flex-1" data-id="element-528">
                {selectedPlatforms.map((sys, i) => {
              const sysProgress = Math.max(0, Math.min(100, progress - i * 10));
              return <div key={sys} data-id="element-529">
                      <div className="flex justify-between text-xs mb-1.5" data-id="element-530">
                        <SystemBadge system={sys as any} data-id="element-531" />
                        <span className="font-mono text-slate-500" data-id="element-532">
                          {Math.floor(sysProgress)}%
                        </span>
                      </div>
                      <ProgressBar progress={sysProgress} active={sysProgress < 100 && progress < 100} data-id="element-533" />
                    </div>;
            })}
              </div>
            </Card> : <Card className="p-6 h-full flex flex-col items-center justify-center text-center bg-slate-50/50 border-dashed" data-id="element-534">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-535">
                <Play className="w-8 h-8 text-slate-400 ml-1" data-id="element-536" />
              </div>
              <h3 className="text-lg font-medium text-slate-900" data-id="element-537">
                Ready to Run
              </h3>
              <p className="text-sm text-slate-500 max-w-sm mt-2" data-id="element-538">
                Configure your job parameters on the left and click Run to start
                processing.
              </p>
            </Card>}
        </div>
      </div>
    </div>;
}
function PlatformSelectCard({
  system,
  selected,
  onClick
}: {
  system: string;
  selected: boolean;
  onClick: () => void;
}) {
  return <div className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${selected ? 'border-electric-500 bg-electric-50/30' : 'border-slate-200 hover:border-slate-300 bg-white'}`} onClick={onClick} data-id="element-539">
      <div className="flex items-center justify-between mb-2" data-id="element-540">
        <SystemBadge system={system as any} showLabel={false} className="scale-125 origin-left" data-id="element-541" />
        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'bg-electric-500 border-electric-500' : 'border-slate-300'}`} data-id="element-542">
          {selected && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-543" />}
        </div>
      </div>
      <div className="text-sm font-medium text-slate-900 capitalize" data-id="element-544">
        {system}
      </div>
      <div className="text-[10px] text-slate-500 mt-1" data-id="element-545">API v3</div>
    </div>;
}
function StatBox({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: string;
}) {
  return <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-546">
      <div className="text-xs text-slate-500 mb-1" data-id="element-547">{label}</div>
      <div className={`text-xl font-semibold font-mono ${color}`} data-id="element-548">{value}</div>
    </div>;
}